import { civilParts, instantFromWall } from '../../lib/timeZone';

/**
 * The window the whole dashboard is read in.
 *
 * This is the rework. On the Flutter dashboard the window belongs to each
 * METRIC — a card stores its own time shape in `groupBy`/`arrangeBy`, fetches
 * its own range, and carries its own shifter — so a page of twelve cards is
 * twelve windows, twelve requests and twelve answers to "compared with when?".
 * Reading two cards against each other meant checking what each one was set to
 * first. Here the page picks once and every card obeys, which makes the cards
 * comparable and collapses the reads from one-per-card to one-per-category.
 *
 * A metric's stored shape is therefore NOT read as a window any more. It stays
 * untouched on the row — it is what the Flutter page still draws from, and
 * opening this screen must not rewrite what a metric means over there — and it
 * is used only as the default offered when a metric is created.
 *
 * Everything here is pure and works in CIVIL DAYS in the tenant's zone, turning
 * them into instants only at the edge. That order matters: "the last 30 days"
 * is a claim about the operator's calendar, and computing it from instants in
 * the browser's zone puts the boundary in the wrong place for every operator
 * who is not sitting in the office.
 */

/** The bucket dispatch groups by. `all` is the snapshot bucket and is not a window. */
export type Bucket = 'day' | 'week' | 'month' | 'quarter' | 'year';

export interface CivilDay {
  y: number;
  m: number;
  d: number;
}

export interface FramePreset {
  key: string;
  label: string;
  /** The grouping this window reads best at. Overridable — see `bucketsFor`. */
  bucket: Bucket;
  /** Grouped in the picker: the short ones, the calendar ones, the long ones. */
  group: 'recent' | 'calendar' | 'long';
}

export interface ResolvedFrame {
  key: string;
  label: string;
  bucket: Bucket;
  from: CivilDay;
  to: CivilDay;
  /** UTC instants on the wire — the tenant day's true edges. */
  fromIso: string;
  toIso: string;
  /**
   * The window immediately before this one, the same number of days long. What
   * a KPI's percentage change is measured against, and never a calendar period
   * of a different length: "23 days of this month vs 31 days of last month"
   * reads as a collapse every first week of every month.
   */
  compare: { from: CivilDay; to: CivilDay; fromIso: string; toIso: string };
  compareLabel: string;
  /** Whole days in the window, inclusive. */
  days: number;
}

/**
 * The presets, in the order the picker lists them.
 *
 * Chosen as the questions an operator actually asks — how did today go, how is
 * the month tracking, how does the year compare — rather than as a ladder of
 * durations. `Yesterday` earns its place because a dispatcher reading at 8am
 * has almost no "today" to read yet.
 */
export const PRESETS: FramePreset[] = [
  { key: 'today', label: 'Today', bucket: 'day', group: 'recent' },
  { key: 'yesterday', label: 'Yesterday', bucket: 'day', group: 'recent' },
  { key: '7d', label: 'Last 7 days', bucket: 'day', group: 'recent' },
  { key: '30d', label: 'Last 30 days', bucket: 'day', group: 'recent' },
  { key: 'mtd', label: 'This month', bucket: 'day', group: 'calendar' },
  { key: 'lastMonth', label: 'Last month', bucket: 'day', group: 'calendar' },
  { key: 'qtd', label: 'This quarter', bucket: 'week', group: 'calendar' },
  { key: 'ytd', label: 'Year to date', bucket: 'month', group: 'long' },
  { key: '12m', label: 'Last 12 months', bucket: 'month', group: 'long' },
];

export const DEFAULT_FRAME = '30d';

export const presetOf = (key: string): FramePreset | undefined =>
  PRESETS.find((preset) => preset.key === key);

/* ---- civil day arithmetic ------------------------------------------------- */

const pad = (value: number) => String(value).padStart(2, '0');

export const dayString = (day: CivilDay): string => `${day.y}-${pad(day.m)}-${pad(day.d)}`;

/** Days since the epoch, so a civil day can be added to and compared as a number. */
const toIndex = (day: CivilDay): number => Math.round(Date.UTC(day.y, day.m - 1, day.d) / 86_400_000);

const fromIndex = (index: number): CivilDay => {
  const date = new Date(index * 86_400_000);
  return { y: date.getUTCFullYear(), m: date.getUTCMonth() + 1, d: date.getUTCDate() };
};

export const addDays = (day: CivilDay, count: number): CivilDay => fromIndex(toIndex(day) + count);

/**
 * Calendar months, clamped to the target month's length — `31 Jan` plus one
 * month is the end of February, not the 3rd of March, which is what plain day
 * arithmetic gives and why "last 12 months" would otherwise start on a
 * different day of the month depending on which month you asked in.
 */
export const addMonths = (day: CivilDay, count: number): CivilDay => {
  const total = day.y * 12 + (day.m - 1) + count;
  const y = Math.floor(total / 12);
  const m = (total % 12) + 1;
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { y, m, d: Math.min(day.d, lastDay) };
};

export const daysBetween = (from: CivilDay, to: CivilDay): number => toIndex(to) - toIndex(from) + 1;

/** Today on the tenant's calendar — not the viewer's. */
export const civilToday = (zone: string | null, now: Date = new Date()): CivilDay => {
  const parts = civilParts(now, zone);
  return { y: parts.year, m: parts.month, d: parts.day };
};

const quarterStart = (day: CivilDay): CivilDay => ({
  y: day.y,
  m: Math.floor((day.m - 1) / 3) * 3 + 1,
  d: 1,
});

/**
 * A tenant civil day's true edges as instants.
 *
 * The end is the next day's start less a millisecond rather than `23:59:59`,
 * because a day that a DST jump makes 23 or 25 hours long still ends exactly
 * where the next one begins — and on the spring-forward day a literal 23:59
 * is an hour and a bit short of it.
 */
export const dayStartIso = (day: CivilDay, zone: string | null): string =>
  (instantFromWall(`${dayString(day)}T00:00`, zone) ?? new Date(0)).toISOString();

export const dayEndIso = (day: CivilDay, zone: string | null): string => {
  const nextStart = instantFromWall(`${dayString(addDays(day, 1))}T00:00`, zone);
  return new Date((nextStart?.getTime() ?? 0) - 1).toISOString();
};

/* ---- resolving ------------------------------------------------------------ */

const boundsFor = (key: string, today: CivilDay): { from: CivilDay; to: CivilDay } => {
  switch (key) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const day = addDays(today, -1);
      return { from: day, to: day };
    }
    case '7d':
      return { from: addDays(today, -6), to: today };
    case 'mtd':
      return { from: { y: today.y, m: today.m, d: 1 }, to: today };
    case 'lastMonth': {
      const start = addMonths({ y: today.y, m: today.m, d: 1 }, -1);
      return { from: start, to: addDays({ y: today.y, m: today.m, d: 1 }, -1) };
    }
    case 'qtd':
      return { from: quarterStart(today), to: today };
    case 'ytd':
      return { from: { y: today.y, m: 1, d: 1 }, to: today };
    case '12m':
      return { from: addDays(addMonths(today, -12), 1), to: today };
    default:
      return { from: addDays(today, -29), to: today };
  }
};

/**
 * What the percentage change says it is against.
 *
 * Worded per frame rather than always "vs previous period", because the
 * comparison is the half of a KPI an operator is most likely to misread — a
 * number that is down 40% against the previous seven days and a number that is
 * down 40% against last year are not the same news.
 */
const compareLabelFor = (key: string, days: number): string => {
  switch (key) {
    case 'today':
      return 'vs yesterday';
    case 'yesterday':
      return 'vs the day before';
    case 'mtd':
      return 'vs the same days last month';
    case 'lastMonth':
      return 'vs the month before';
    case 'qtd':
      return 'vs the same days last quarter';
    case 'ytd':
      return 'vs the same days last year';
    default:
      return `vs previous ${days} days`;
  }
};

/**
 * A preset (or a custom range) turned into everything a read needs.
 *
 * `bucketOverride` is the operator's Day/Week/Month toggle. It is CLAMPED
 * rather than trusted: three days grouped by month is one bar, and a year
 * grouped by day is 365 of them squeezed into a card — both are requests the
 * picker should not have offered, so the clamp is here rather than in the
 * component that draws it.
 */
export const resolveFrame = (
  key: string,
  zone: string | null,
  options: { now?: Date; custom?: { from: CivilDay; to: CivilDay }; bucketOverride?: Bucket } = {},
): ResolvedFrame => {
  const today = civilToday(zone, options.now ?? new Date());
  const preset = presetOf(key);
  const isCustom = key === 'custom' && options.custom;

  const { from, to } = isCustom
    ? normalizeCustom(options.custom as { from: CivilDay; to: CivilDay })
    : boundsFor(preset?.key ?? DEFAULT_FRAME, today);

  const days = daysBetween(from, to);
  const natural = isCustom ? naturalBucket(days) : (preset?.bucket ?? 'day');
  const bucket = options.bucketOverride
    ? clampBucket(options.bucketOverride, days)
    : clampBucket(natural, days);

  // The same number of days, ending the day before this window opens.
  const compareTo = addDays(from, -1);
  const compareFrom = addDays(compareTo, -(days - 1));

  return {
    key: isCustom ? 'custom' : (preset?.key ?? DEFAULT_FRAME),
    label: isCustom ? `${dayString(from)} – ${dayString(to)}` : (preset?.label ?? 'Last 30 days'),
    bucket,
    from,
    to,
    fromIso: dayStartIso(from, zone),
    toIso: dayEndIso(to, zone),
    compare: {
      from: compareFrom,
      to: compareTo,
      fromIso: dayStartIso(compareFrom, zone),
      toIso: dayEndIso(compareTo, zone),
    },
    compareLabel: isCustom ? `vs previous ${days} days` : compareLabelFor(key, days),
    days,
  };
};

/** Backwards ranges are the picker's two fields in the wrong order, not an error worth refusing. */
const normalizeCustom = (custom: { from: CivilDay; to: CivilDay }) =>
  toIndex(custom.to) < toIndex(custom.from)
    ? { from: custom.to, to: custom.from }
    : { from: custom.from, to: custom.to };

/**
 * Roughly how many buckets a grouping yields over `days`. The ceilings are what
 * `bucketsFor` reads to decide which groupings are worth offering at all.
 */
const BUCKET_DAYS: Record<Bucket, number> = { day: 1, week: 7, month: 30, quarter: 91, year: 365 };

const ORDER: Bucket[] = ['day', 'week', 'month', 'quarter', 'year'];

/** The grouping a custom range reads best at: aim for somewhere near 10–40 marks. */
export const naturalBucket = (days: number): Bucket => {
  if (days <= 45) return 'day';
  if (days <= 180) return 'week';
  if (days <= 900) return 'month';
  if (days <= 2000) return 'quarter';
  return 'year';
};

/**
 * Which groupings this window can honestly be drawn at.
 *
 * A grouping is offered when the window holds at least two of its buckets —
 * one bucket is not a trend, it is a single number wearing a chart's clothes —
 * and at most 120, past which the bars are thinner than their gaps.
 */
export const bucketsFor = (days: number): Bucket[] =>
  ORDER.filter((bucket) => {
    const count = days / BUCKET_DAYS[bucket];
    return count >= 1.5 && count <= 120;
  });

export const clampBucket = (bucket: Bucket, days: number): Bucket => {
  const allowed = bucketsFor(days);
  if (allowed.includes(bucket)) return bucket;
  // A window too short for any grouping (one or two days) is still a day chart.
  if (!allowed.length) return 'day';
  // Otherwise fall to the nearest offered grouping rather than to the default,
  // so switching from a 30-day to a 7-day window keeps the shape it can keep.
  const wanted = ORDER.indexOf(bucket);
  return allowed.reduce((best, candidate) =>
    Math.abs(ORDER.indexOf(candidate) - wanted) < Math.abs(ORDER.indexOf(best) - wanted) ? candidate : best,
  );
};

/**
 * The same window, moved one of its own lengths back or forward.
 *
 * What the dashboard's per-metric shifter did, for the page's window: a
 * dispatcher reading Today wants yesterday next, and one reading Last 7 days
 * wants the week before — not a date picker. Stepping by the window's OWN
 * length is what makes the two comparable, which is the whole reason the
 * window is shared in the first place.
 *
 * It answers a civil range rather than a frame key, because a stepped window
 * is no longer the preset it started from: "Today" moved back once is a
 * specific day, and calling it Today would be a lie the next render corrects.
 * The caller stores it as the custom range.
 */
export const shiftFrame = (
  frame: { from: CivilDay; to: CivilDay; days: number },
  direction: -1 | 1,
): { from: CivilDay; to: CivilDay } => {
  const step = Math.max(1, frame.days) * direction;
  return { from: addDays(frame.from, step), to: addDays(frame.to, step) };
};

/**
 * Whether the window can move that way.
 *
 * Forward stops at today: there are no jobs tomorrow, and a window that runs
 * into the future reads as a collapse in every figure on the page. Backward is
 * always allowed — the data thins out, which is an answer rather than an error.
 */
export const canShiftFrame = (
  frame: { from: CivilDay; to: CivilDay; days: number },
  direction: -1 | 1,
  zone: string | null,
  now: Date = new Date(),
): boolean => {
  if (direction === -1) return true;
  return toIndex(frame.to) < toIndex(civilToday(zone, now));
};

/** A single day gets this much context behind it before a chart is worth drawing. */
export const CONTEXT_DAYS = 7;

/**
 * The range a CHART should be read over, which is not always the page's window.
 *
 * One day is a legitimate thing to ask a KPI — "how many today" is the
 * question a dispatcher opens the page with — and a hopeless thing to ask a
 * chart, which answers it with one bar and no shape at all. So a one-day
 * window reads a week ENDING on that day, and the day itself is highlighted:
 * the figure the operator asked for is still the one picked out, and it now has
 * something to be read against.
 *
 * Every other window is its own context, so nothing extra is fetched and the
 * page costs exactly what it did.
 */
export const chartContext = (
  frame: { from: CivilDay; to: CivilDay; days: number },
): { from: CivilDay; to: CivilDay; widened: boolean } => {
  if (frame.days !== 1) return { from: frame.from, to: frame.to, widened: false };
  return { from: addDays(frame.to, -(CONTEXT_DAYS - 1)), to: frame.to, widened: true };
};
